import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, FolderIcon, ClockIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { controlProyectosService, empleadoService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatoFechaDMY } from '../utils/dateUtils';
import { datetimeBolsaHorasALocalInput, formatoDatetimeBolsaHoras } from '../utils/bolsaHorasDateUtils';

const REQUERIDO_POR_OPTS = [
  { value: 'ricardo_martinez', label: 'Ricardo Martínez' },
  { value: 'rodrigo_loayza', label: 'Rodrigo Loayza' },
  { value: 'juan_pena', label: 'Juan Peña' },
  { value: 'magali_sevillano', label: 'Magali Sevillano' },
  { value: 'enrique_agapito', label: 'Enrique Agapito' },
  { value: 'luis_aguayo', label: 'Luis Aguayo' },
  { value: 'stephanie_agapito', label: 'Stephanie Agapito' },
  { value: 'jeff_pena', label: 'Jeff Peña' },
  { value: 'otros', label: 'Otros' }
];

const REQUERIDO_POR_FORM_OPTS = REQUERIDO_POR_OPTS.filter(
  (o) => !['rodrigo_loayza', 'juan_pena'].includes(o.value)
);

const EST_APROB_BADGE = {
  no_aplica: 'bg-slate-100 text-slate-600',
  pendiente: 'bg-amber-100 text-amber-800',
  aprobada: 'bg-emerald-100 text-emerald-800',
  rechazada: 'bg-red-100 text-red-800'
};

const EST_APROB_LABEL = {
  no_aplica: 'N/A',
  pendiente: 'Pendiente',
  aprobada: 'Aprobada',
  rechazada: 'Rechazada'
};

const EST_PROY = [
  { value: 'finalizado', label: 'Finalizado' },
  { value: 'en_curso', label: 'En curso' },
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'perdido', label: 'Perdido' }
];

const PRIOR = [
  { value: 'baja', label: 'Baja' },
  { value: 'media', label: 'Medio' },
  { value: 'alta', label: 'Alta' }
];

const EST_ACT = [
  { value: 'no_iniciado', label: 'No iniciado' },
  { value: 'en_progreso', label: 'En progreso' },
  { value: 'cerrado', label: 'Cerrado' }
];

const SIT_PAGO = [
  { value: 'pagado', label: 'Pagado' },
  { value: 'pendiente', label: 'Pendiente' }
];

function previewHorasIniFin(iniLocal, finLocal) {
  if (!iniLocal || !finLocal || iniLocal.length < 16 || finLocal.length < 16) return null;
  const a = new Date(iniLocal);
  const b = new Date(finLocal);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b <= a) return null;
  return Math.round((((b - a) / 3600000) * 100)) / 100;
}

/** IDs numéricos únicos para selección de consultores (checkboxes) */
function normalizaIdsConsultores(arr) {
  if (!Array.isArray(arr)) return [];
  return [...new Set(arr.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0))].sort((a, b) => a - b);
}

function textoCoincideBusqueda(textos, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = textos
    .flatMap((t) => (t == null ? [] : [String(t)]))
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

const BUSQUEDA_PROYECTO_CAMPOS = [
  { value: 'todos', label: 'Todos los campos' },
  { value: 'empresa', label: 'Empresa' },
  { value: 'proyecto', label: 'Proyecto' },
  { value: 'encargado', label: 'Encargado' },
  { value: 'consultores', label: 'Consultores' },
  { value: 'estado', label: 'Estado' },
  { value: 'fechas', label: 'Inicio–Fin' }
];

const BUSQUEDA_ACTIVIDAD_CAMPOS = [
  { value: 'todos', label: 'Todos los campos' },
  { value: 'id', label: 'ID' },
  { value: 'proyecto', label: 'Proyecto / empresa' },
  { value: 'consultor', label: 'Consultor' },
  { value: 'requerido_por', label: 'Requerido por' },
  { value: 'descripcion', label: 'Descripción' },
  { value: 'estado', label: 'Estado actividad' },
  { value: 'aprobacion', label: 'Aprobación' },
  { value: 'fechas', label: 'Inicio–Fin' }
];

function placeholderBusqueda(campo, opciones, fallback) {
  if (campo === 'todos') return fallback;
  const opt = opciones.find((o) => o.value === campo);
  return opt ? `Buscar en ${opt.label.toLowerCase()}…` : fallback;
}

const proyectoVacio = () => ({
  empresa: '',
  proyecto: '',
  fecha_inicio: '',
  fecha_fin: '',
  consultores_empleado_ids: [],
  horas_asignadas: '',
  estado: 'pendiente',
  encargado_empleado_id: '',
  detalles: ''
});

const actividadVacia = () => ({
  proyecto_id: '',
  /* Solo lo usa admin/gestor cuando registra una actividad en nombre de
   * otro consultor. Para el resto de roles queda vacío y el backend asigna
   * al propio usuario que está creando el registro. */
  consultor_asignado_id: '',
  requerido_por: 'ricardo_martinez',
  requerido_por_otros: '',
  descripcion_actividad: '',
  prioridad: 'media',
  fecha_hora_inicio: '',
  fecha_hora_fin: '',
  estado: 'no_iniciado',
  comentarios: '',
  situacion_pago: 'pendiente',
  /* Modo de horas:
   *   - horas_manual=false (default): las horas se calculan automáticamente
   *     a partir de fecha_hora_inicio y fecha_hora_fin (previewHorasIniFin).
   *   - horas_manual=true: el usuario las ingresa a mano en horas_trabajadas_manual.
   *     Igualmente sigue siendo obligatorio indicar el rango de fechas. */
  horas_manual: false,
  horas_trabajadas_manual: ''
});

const ControlProyectos = () => {
  const { usuario, puedeGestionarProyectosCp, esAdmin } = useAuth();
  const puedeProy = puedeGestionarProyectosCp();
  const [tab, setTab] = useState(puedeProy ? 'proyectos' : 'actividades');
  const [cargando, setCargando] = useState(true);
  const [proyectos, setProyectos] = useState([]);
  const [misProyectos, setMisProyectos] = useState([]);
  const [actividades, setActividades] = useState([]);
  const [consultores, setConsultores] = useState([]);
  const [filtroConsultor, setFiltroConsultor] = useState('');
  const [proyectoEditId, setProyectoEditId] = useState(null);
  const [actividadEditId, setActividadEditId] = useState(null);
  const [actividadEditMeta, setActividadEditMeta] = useState(null);
  const [proyForm, setProyForm] = useState(proyectoVacio);
  const [actForm, setActForm] = useState(actividadVacia);
  const [filtroProyectoAct, setFiltroProyectoAct] = useState('');
  const [busquedaProyectos, setBusquedaProyectos] = useState('');
  const [campoBusquedaProyectos, setCampoBusquedaProyectos] = useState('todos');
  const [busquedaActividades, setBusquedaActividades] = useState('');
  const [campoBusquedaActividades, setCampoBusquedaActividades] = useState('todos');
  const [pendientesAprobacion, setPendientesAprobacion] = useState([]);
  const [rechazoId, setRechazoId] = useState(null);
  const [rechazoComentario, setRechazoComentario] = useState('');
  const [procesandoAprobacion, setProcesandoAprobacion] = useState(false);
  const nombreUsuario = `${usuario?.nombres || ''} ${usuario?.apellidos || ''}`.trim();
  const [empleadosEncargado, setEmpleadosEncargado] = useState([]);

  const cargarMisProyectos = useCallback(async () => {
    const { data } = await controlProyectosService.misProyectos();
    setMisProyectos(data.data || []);
  }, []);

  const cargarProyectosTodos = useCallback(async () => {
    if (!puedeProy) return;
    const { data } = await controlProyectosService.listarProyectos();
    setProyectos(data.data || []);
  }, [puedeProy]);

  const consultoresFiltrados = useMemo(() => {
    const q = filtroConsultor.trim().toLowerCase();
    if (!q) return consultores;
    return consultores.filter((c) => {
      const nombre = (c.nombre_completo || '').toLowerCase();
      const mail = (c.email || '').toLowerCase();
      return nombre.includes(q) || mail.includes(q);
    });
  }, [consultores, filtroConsultor]);

  const cargarActividades = useCallback(async () => {
    const params = {};
    if (filtroProyectoAct) params.proyecto_id = filtroProyectoAct;
    const { data } = await controlProyectosService.listarActividades(params);
    setActividades(data.data || []);
  }, [filtroProyectoAct]);

  const cargarPendientesAprobacion = useCallback(async () => {
    try {
      const { data } = await controlProyectosService.listarPendientesAprobacionActividades();
      setPendientesAprobacion(data.data || []);
    } catch {
      setPendientesAprobacion([]);
    }
  }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setCargando(true);
      try {
        await cargarMisProyectos();
        if (puedeProy) await cargarProyectosTodos();
      } catch {
        if (!cancel) toast.error('Error al cargar control de proyectos. ¿Ejecutaste la migración SQL?');
      } finally {
        if (!cancel) setCargando(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [puedeProy, cargarMisProyectos, cargarProyectosTodos]);

  useEffect(() => {
    if (!puedeProy && tab === 'proyectos') setTab('actividades');
  }, [puedeProy, tab]);

  useEffect(() => {
    cargarPendientesAprobacion();
  }, [cargarPendientesAprobacion]);

  const requeridoPorSelectOpts = useMemo(() => {
    const base = [...REQUERIDO_POR_FORM_OPTS];
    const cur = actForm.requerido_por;
    if (cur && !base.some((o) => o.value === cur)) {
      const leg = REQUERIDO_POR_OPTS.find((o) => o.value === cur);
      if (leg) base.unshift(leg);
    }
    return base;
  }, [actForm.requerido_por]);

  useEffect(() => {
    let cancel = false;
    if (!puedeProy) {
      setConsultores([]);
      return undefined;
    }
    (async () => {
      try {
        const params = proyectoEditId ? { proyecto_id: proyectoEditId } : {};
        const { data } = await controlProyectosService.consultoresSelect(params);
        if (!cancel) setConsultores(data.data || []);
      } catch {
        if (!cancel) toast.error('Error al cargar listado de consultores.');
      }
    })();
    return () => {
      cancel = true;
    };
  }, [puedeProy, proyectoEditId]);

  useEffect(() => {
    if (!puedeProy) {
      setEmpleadosEncargado([]);
      return undefined;
    }
    let cancel = false;
    (async () => {
      try {
        const { data } = await empleadoService.listar({ activo: true });
        const rows = Array.isArray(data.data)
          ? data.data.filter((e) => e.email && String(e.email).trim())
          : [];
        if (!cancel) setEmpleadosEncargado(rows);
      } catch {
        if (!cancel) toast.error('Error al cargar listado para encargado del proyecto.');
      }
    })();
    return () => {
      cancel = true;
    };
  }, [puedeProy]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        await cargarActividades();
      } catch {
        if (!cancel) toast.error('Error al cargar actividades.');
      }
    })();
    return () => {
      cancel = true;
    };
  }, [cargarActividades]);

  const horasPreviewAct = useMemo(
    () => previewHorasIniFin(actForm.fecha_hora_inicio, actForm.fecha_hora_fin),
    [actForm.fecha_hora_inicio, actForm.fecha_hora_fin]
  );

  /* Lista de proyectos seleccionables al registrar/editar una actividad.
   *   - Admin / cuenta gestora: todos los proyectos.
   *   - Resto: solo los proyectos donde el usuario está asignado como consultor. */
  const proyectosParaActividad = useMemo(() => {
    if (puedeProy && Array.isArray(proyectos) && proyectos.length) return proyectos;
    return misProyectos;
  }, [puedeProy, proyectos, misProyectos]);

  /* Consultores asignados al proyecto seleccionado actualmente en el form.
   * Se usa para que admin pueda elegir a quién asignarle la actividad. */
  const consultoresDelProyectoActivo = useMemo(() => {
    const pid = Number(actForm.proyecto_id);
    if (!pid) return [];
    const proy = proyectosParaActividad.find((p) => Number(p.id) === pid);
    if (!proy) return [];
    if (Array.isArray(proy.consultores) && proy.consultores.length) {
      return proy.consultores;
    }
    /* Fallback: si solo viene la lista de IDs, derivamos a partir del catálogo
     * de consultores cargado para el módulo. */
    if (Array.isArray(proy.consultores_empleado_ids)) {
      return proy.consultores_empleado_ids
        .map((id) => {
          const c = (consultores || []).find((x) => Number(x.id) === Number(id));
          return c ? { id: c.id, nombre_completo: c.nombre_completo, email: c.email } : null;
        })
        .filter(Boolean);
    }
    return [];
  }, [actForm.proyecto_id, proyectosParaActividad, consultores]);

  const resetProyForm = () => {
    setProyForm(proyectoVacio());
    setProyectoEditId(null);
    setFiltroConsultor('');
  };

  const submitProyecto = async (e) => {
    e.preventDefault();
    if (!puedeProy) {
      toast.error('Solo pueden guardar proyectos el administrador o la cuenta corporativa autorizada.');
      return;
    }
    try {
      const body = {
        empresa: proyForm.empresa,
        proyecto: proyForm.proyecto,
        fecha_inicio: proyForm.fecha_inicio,
        fecha_fin: proyForm.fecha_fin,
        horas_asignadas: parseFloat(String(proyForm.horas_asignadas).replace(',', '.')) || 0,
        estado: proyForm.estado,
        detalles: proyForm.detalles,
        encargado_empleado_id: parseInt(String(proyForm.encargado_empleado_id), 10),
        consultores_empleado_ids: Array.isArray(proyForm.consultores_empleado_ids)
          ? normalizaIdsConsultores(proyForm.consultores_empleado_ids)
          : []
      };
      if (!body.empresa.trim() || !body.proyecto.trim() || !body.fecha_inicio || !body.fecha_fin) {
        toast.error('Complete los campos obligatorios del proyecto.');
        return;
      }
      if (!Number.isFinite(body.encargado_empleado_id) || body.encargado_empleado_id < 1) {
        toast.error('Seleccione el encargado del proyecto (recibirá correos ante cambios en el registro de horas).');
        return;
      }
      if (!body.consultores_empleado_ids.length) {
        toast.error('Seleccione al menos un consultor del portal.');
        return;
      }
      if (proyectoEditId) {
        await controlProyectosService.actualizarProyecto(proyectoEditId, body);
        toast.success('Proyecto actualizado.');
      } else {
        await controlProyectosService.crearProyecto(body);
        toast.success('Proyecto creado.');
      }
      resetProyForm();
      await cargarProyectosTodos();
      await cargarMisProyectos();
    } catch (err) {
      toast.error(err.response?.data?.mensaje || 'Error al guardar proyecto');
    }
  };

  const submitActividad = async (e) => {
    e.preventDefault();
    try {
      /* Resolvemos las horas según el modo elegido en el switch. */
      let horasResueltas;
      if (actForm.horas_manual) {
        const txt = String(actForm.horas_trabajadas_manual || '').trim().replace(',', '.');
        const n = Number(txt);
        if (!txt || !Number.isFinite(n) || n <= 0) {
          toast.error('Ingrese las horas trabajadas (valor mayor que 0).');
          return;
        }
        horasResueltas = Math.round(n * 100) / 100;
      } else {
        if (horasPreviewAct == null) {
          toast.error('La fecha y hora de fin debe ser posterior a la de inicio para calcular las horas.');
          return;
        }
        horasResueltas = horasPreviewAct;
      }

      const body = {
        ...actForm,
        proyecto_id: parseInt(actForm.proyecto_id, 10),
        horas_trabajadas: horasResueltas
      };
      /* Estos campos son solo UI; no los enviamos al backend. */
      delete body.horas_manual;
      delete body.horas_trabajadas_manual;

      /* `consultor_asignado_id` solo viaja al backend si el usuario es gestor
       * y eligió uno explícito; si no, lo quitamos para que el backend deje el
       * comportamiento por defecto (al crear: el propio usuario; al editar: sin cambios). */
      if (puedeProy && actForm.consultor_asignado_id) {
        body.consultor_asignado_id = parseInt(actForm.consultor_asignado_id, 10);
      } else {
        delete body.consultor_asignado_id;
      }

      if (body.requerido_por === 'otros') {
        const t = String(body.requerido_por_otros || '').trim();
        if (!t) {
          toast.error('Indique el nombre cuando selecciona Otros.');
          return;
        }
        body.requerido_por_otros = t.slice(0, 280);
      } else {
        body.requerido_por_otros = null;
      }
      if (!body.proyecto_id) {
        toast.error('Seleccione un proyecto.');
        return;
      }
      if (!body.descripcion_actividad.trim()) {
        toast.error('Indique la descripción de la actividad.');
        return;
      }
      if (!body.fecha_hora_inicio || !body.fecha_hora_fin) {
        toast.error('Indique inicio y fin.');
        return;
      }
      if (!esAdmin()) {
        delete body.situacion_pago;
      }
      if (actividadEditId) {
        await controlProyectosService.actualizarActividad(actividadEditId, body);
        toast.success('Actividad actualizada.');
      } else {
        await controlProyectosService.crearActividad(body);
        toast.success('Actividad registrada.');
      }
      setActForm(actividadVacia());
      setActividadEditId(null);
      setActividadEditMeta(null);
      await cargarActividades();
      await cargarPendientesAprobacion();
    } catch (err) {
      toast.error(err.response?.data?.mensaje || 'Error al guardar actividad');
    }
  };

  const abrirEditProyecto = (p) => {
    if (!puedeProy) return;
    setProyectoEditId(p.id);
    setFiltroConsultor('');
    setProyForm({
      empresa: p.empresa || '',
      proyecto: p.proyecto || '',
      fecha_inicio: p.fecha_inicio ? String(p.fecha_inicio).slice(0, 10) : '',
      fecha_fin: p.fecha_fin ? String(p.fecha_fin).slice(0, 10) : '',
        consultores_empleado_ids: normalizaIdsConsultores(
          p.consultores_empleado_ids || p.consultores?.map((c) => c.id) || []
        ),
        horas_asignadas: String(p.horas_asignadas ?? ''),
        estado: p.estado || 'pendiente',
        encargado_empleado_id:
          p.encargado_empleado_id != null && p.encargado_empleado_id !== ''
            ? String(p.encargado_empleado_id)
            : '',
        detalles: p.detalles || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const eliminarProyectoFila = async (p) => {
    if (!puedeProy) return;
    const nombre = `${p.proyecto || '(sin nombre)'} — ${p.empresa || ''}`;
    const okConfirm = window.confirm(
      `¿Eliminar este proyecto?\n\n${nombre}\n\nSe eliminarán también las horas/actividades registradas para este proyecto. Esta acción no se puede deshacer.`
    );
    if (!okConfirm) return;
    try {
      await controlProyectosService.eliminarProyecto(p.id);
      toast.success('Proyecto eliminado.');
      if (proyectoEditId === p.id) resetProyForm();
      await cargarProyectosTodos();
      await cargarMisProyectos();
      await cargarActividades();
    } catch (err) {
      toast.error(err.response?.data?.mensaje || 'No se pudo eliminar el proyecto.');
    }
  };

  const abrirEditActividad = (a) => {
    setActividadEditId(a.id);
    setActividadEditMeta({
      estado_aprobacion: a.estado_aprobacion,
      comentario_aprobacion: a.comentario_aprobacion
    });
    const fhiLocal = datetimeBolsaHorasALocalInput(a.fecha_hora_inicio);
    const fhfLocal = datetimeBolsaHorasALocalInput(a.fecha_hora_fin);

    /* Detección automática del modo: si las horas guardadas difieren
     * (con tolerancia) del cálculo por fechas, asumimos que se ingresaron
     * manualmente y abrimos el form en modo manual para no perder ese valor. */
    const horasAuto = previewHorasIniFin(fhiLocal, fhfLocal);
    const horasGuardadas = Number(a.horas_trabajadas);
    const esManual =
      Number.isFinite(horasGuardadas) &&
      (horasAuto == null || Math.abs(horasAuto - horasGuardadas) > 0.01);

    setActForm({
      proyecto_id: String(a.proyecto_id),
      /* Solo precargamos el consultor si el usuario actual puede cambiarlo
       * (admin/gestor). Para el resto el campo no se muestra y queda vacío. */
      consultor_asignado_id:
        puedeProy && a.consultor_asignado_id != null ? String(a.consultor_asignado_id) : '',
      requerido_por: a.requerido_por,
      requerido_por_otros: a.requerido_por === 'otros' ? a.requerido_por_otros || '' : '',
      descripcion_actividad: a.descripcion_actividad || '',
      prioridad: a.prioridad || 'media',
      fecha_hora_inicio: fhiLocal,
      fecha_hora_fin: fhfLocal,
      estado: a.estado || 'no_iniciado',
      comentarios: a.comentarios || '',
      situacion_pago: a.situacion_pago || 'pendiente',
      horas_manual: esManual,
      horas_trabajadas_manual: esManual ? String(horasGuardadas) : ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const labelReqActividad = useCallback((a) => {
    if (!a) return '';
    if (a.requerido_por === 'otros') {
      const t = String(a.requerido_por_otros || '').trim();
      if (t) return t;
    }
    return REQUERIDO_POR_OPTS.find((x) => x.value === a.requerido_por)?.label || a.requerido_por;
  }, []);

  const badgeAprobacion = (est) => {
    const k = est || 'no_aplica';
    const cls = EST_APROB_BADGE[k] || EST_APROB_BADGE.no_aplica;
    const lbl = EST_APROB_LABEL[k] || k;
    return (
      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
        {lbl}
      </span>
    );
  };

  const puedeEliminarActividad = useCallback(
    (a) => {
      if (!a) return false;
      if (puedeProy) return true;
      return Number(a.consultor_asignado_id) === Number(usuario?.id);
    },
    [puedeProy, usuario?.id]
  );

  const eliminarActividadFila = async (a) => {
    if (!a || !puedeEliminarActividad(a)) return;
    const titulo = a.proyecto_nombre || `actividad #${a.id}`;
    const okConfirm = window.confirm(
      `¿Eliminar el registro #${a.id}?\n\n${titulo}\n${Number(a.horas_trabajadas).toFixed(2)} h\n\nEsta acción no se puede deshacer.`
    );
    if (!okConfirm) return;
    try {
      await controlProyectosService.eliminarActividad(a.id);
      toast.success('Actividad eliminada.');
      if (actividadEditId === a.id) {
        setActividadEditId(null);
        setActividadEditMeta(null);
        setActForm(actividadVacia());
      }
      await cargarActividades();
      await cargarPendientesAprobacion();
    } catch (err) {
      toast.error(err.response?.data?.mensaje || 'No se pudo eliminar la actividad.');
    }
  };

  const aprobarPendiente = async (id) => {
    setProcesandoAprobacion(true);
    try {
      const { data } = await controlProyectosService.aprobarActividadBolsaHoras(id);
      if (data.success) {
        toast.success('Actividad aprobada.');
        await cargarPendientesAprobacion();
        await cargarActividades();
      } else toast.error(data.mensaje || 'No se pudo aprobar.');
    } catch (e) {
      toast.error(e.response?.data?.mensaje || 'Error al aprobar.');
    } finally {
      setProcesandoAprobacion(false);
    }
  };

  const confirmarRechazo = async () => {
    if (!rechazoId) return;
    setProcesandoAprobacion(true);
    try {
      const { data } = await controlProyectosService.rechazarActividadBolsaHoras(rechazoId, rechazoComentario);
      if (data.success) {
        toast.success('Actividad rechazada.');
        setRechazoId(null);
        setRechazoComentario('');
        await cargarPendientesAprobacion();
        await cargarActividades();
      } else toast.error(data.mensaje || 'No se pudo rechazar.');
    } catch (e) {
      toast.error(e.response?.data?.mensaje || 'Error al rechazar.');
    } finally {
      setProcesandoAprobacion(false);
    }
  };
  const labelEstProy = useCallback((key) => EST_PROY.find((x) => x.value === key)?.label || key, []);

  const textosProyectoBusqueda = useCallback(
    (p, campo) => {
      switch (campo) {
        case 'empresa':
          return [p.empresa];
        case 'proyecto':
          return [p.proyecto];
        case 'encargado':
          return [p.encargado_nombre, p.encargado_email];
        case 'consultores':
          return [p.consultores_nombres];
        case 'estado':
          return [labelEstProy(p.estado), p.estado];
        case 'fechas':
          return [formatoFechaDMY(p.fecha_inicio), formatoFechaDMY(p.fecha_fin)];
        default:
          return [
            p.empresa,
            p.proyecto,
            p.encargado_nombre,
            p.encargado_email,
            p.consultores_nombres,
            labelEstProy(p.estado),
            p.estado,
            p.horas_asignadas,
            formatoFechaDMY(p.fecha_inicio),
            formatoFechaDMY(p.fecha_fin)
          ];
      }
    },
    [labelEstProy]
  );

  const textosActividadBusqueda = useCallback(
    (a, campo) => {
      switch (campo) {
        case 'id':
          return [a.id];
        case 'proyecto':
          return [a.empresa_nombre, a.proyecto_nombre];
        case 'consultor':
          return [a.consultor_nombre];
        case 'requerido_por':
          return [labelReqActividad(a), a.requerido_por, a.requerido_por_otros];
        case 'descripcion':
          return [a.descripcion_actividad, a.comentarios];
        case 'estado':
          return [a.estado, EST_ACT.find((x) => x.value === a.estado)?.label];
        case 'aprobacion':
          return [a.estado_aprobacion, EST_APROB_LABEL[a.estado_aprobacion]];
        case 'fechas':
          return [
            formatoDatetimeBolsaHoras(a.fecha_hora_inicio),
            formatoDatetimeBolsaHoras(a.fecha_hora_fin)
          ];
        default:
          return [
            a.id,
            a.empresa_nombre,
            a.proyecto_nombre,
            a.consultor_nombre,
            labelReqActividad(a),
            a.requerido_por,
            a.requerido_por_otros,
            a.descripcion_actividad,
            a.comentarios,
            a.estado,
            EST_ACT.find((x) => x.value === a.estado)?.label,
            a.estado_aprobacion,
            EST_APROB_LABEL[a.estado_aprobacion],
            a.situacion_pago,
            SIT_PAGO.find((x) => x.value === a.situacion_pago)?.label,
            a.horas_trabajadas,
            formatoDatetimeBolsaHoras(a.fecha_hora_inicio),
            formatoDatetimeBolsaHoras(a.fecha_hora_fin)
          ];
      }
    },
    [labelReqActividad]
  );

  const proyectosFiltrados = useMemo(() => {
    const q = busquedaProyectos.trim();
    if (!q) return proyectos;
    return proyectos.filter((p) =>
      textoCoincideBusqueda(textosProyectoBusqueda(p, campoBusquedaProyectos), q)
    );
  }, [proyectos, busquedaProyectos, campoBusquedaProyectos, textosProyectoBusqueda]);

  const actividadesFiltradas = useMemo(() => {
    const q = busquedaActividades.trim();
    if (!q) return actividades;
    return actividades.filter((a) =>
      textoCoincideBusqueda(textosActividadBusqueda(a, campoBusquedaActividades), q)
    );
  }, [actividades, busquedaActividades, campoBusquedaActividades, textosActividadBusqueda]);

  return (
    <div className="max-w-6xl mx-auto">
      <Link
        to="/portal"
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-indigo-600 mb-8 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Volver al portal
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/25 shrink-0">
            <FolderIcon className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Bolsa de Horas</h1>
            <p className="text-sm text-slate-500 mt-1">
              Crear proyectos y ver el listado global solo pueden el{' '}
              <span className="font-medium text-slate-700">administrador</span> y la cuenta de gestión de bolsa (
              habitualmente asistente@prayaga.biz). El equipo registra{' '}
              <span className="font-medium text-slate-700">Actividades</span>.
            </p>
          </div>
        </div>
        {esAdmin() && (
          <>
            <Link
              to="/admin/control-proyectos-costo-hora"
              className="text-sm font-medium text-indigo-600 hover:text-indigo-800 bg-indigo-50 px-4 py-2 rounded-xl border border-indigo-100"
            >
              Costo por hora (consultores)
            </Link>
            <Link
              to="/admin/control-proyectos-locadores"
              className="text-sm font-medium text-amber-800 hover:text-amber-950 bg-amber-50 px-4 py-2 rounded-xl border border-amber-100"
            >
              Locadores (aprobación)
            </Link>
          </>
        )}
        <Link
          to="/control-proyectos/reporte"
          className="text-sm font-medium text-violet-700 hover:text-violet-900 bg-violet-50 px-4 py-2 rounded-xl border border-violet-100"
        >
          Reportes
        </Link>
      </div>

      <div className="flex gap-2 mb-6">
        {puedeProy && (
          <button
            type="button"
            onClick={() => setTab('proyectos')}
            className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors ${tab === 'proyectos' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            Proyectos
          </button>
        )}
        <button
          type="button"
          onClick={() => setTab('actividades')}
          className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${tab === 'actividades' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
        >
          <ClockIcon className="w-4 h-4" />
          Actividades (registro de horas)
        </button>
        {pendientesAprobacion.length > 0 && (
          <button
            type="button"
            onClick={() => setTab('aprobaciones')}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${tab === 'aprobaciones' ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200'}`}
          >
            Pendientes ({pendientesAprobacion.length})
          </button>
        )}
      </div>

      {cargando ? (
        <p className="text-slate-500">Cargando…</p>
      ) : (
        <>
          {tab === 'proyectos' && puedeProy && (
            <>
              <form onSubmit={submitProyecto} className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6 mb-8 space-y-4">
                  <h2 className="text-lg font-semibold text-slate-800">
                    {proyectoEditId ? `Editar proyecto #${proyectoEditId}` : 'Nuevo proyecto'}
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Empresa *</label>
                      <input
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        value={proyForm.empresa}
                        onChange={(e) => setProyForm((f) => ({ ...f, empresa: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Proyecto *</label>
                      <input
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        value={proyForm.proyecto}
                        onChange={(e) => setProyForm((f) => ({ ...f, proyecto: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Fecha inicio *</label>
                      <input
                        type="date"
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        value={proyForm.fecha_inicio}
                        onChange={(e) => setProyForm((f) => ({ ...f, fecha_inicio: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Fecha fin *</label>
                      <input
                        type="date"
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        value={proyForm.fecha_fin}
                        onChange={(e) => setProyForm((f) => ({ ...f, fecha_fin: e.target.value }))}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Consultores asignados * <span className="text-slate-400 font-normal">(marca uno o más)</span>
                      </label>
                      {consultores.length === 0 ? (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                          No hay consultores en el catálogo. Quien deba asignarse a proyectos debe tener activo{' '}
                          <strong>«Consultor en control de proyectos»</strong> en Administración de usuarios (admin). Si
                          acabas de desplegar, ejecuta la migración SQL{' '}
                          <code className="text-[10px] bg-amber-100 px-1 rounded">20260506_empleados_es_consultor_cp</code>.
                        </p>
                      ) : (
                        <>
                          <input
                            type="search"
                            placeholder="Filtrar por nombre o correo…"
                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm mb-3"
                            value={filtroConsultor}
                            onChange={(e) => setFiltroConsultor(e.target.value)}
                          />
                          <div className="max-h-48 overflow-y-auto rounded-xl border border-slate-200 p-3 space-y-2 bg-slate-50/80">
                            {consultoresFiltrados.length === 0 ? (
                              <p className="text-xs text-slate-500">Nadie coincide con el filtro.</p>
                            ) : (
                              consultoresFiltrados.map((c) => {
                                const id = Number(c.id);
                                const checked = proyForm.consultores_empleado_ids.includes(id);
                                return (
                                  <label
                                    key={c.id}
                                    className="flex items-start gap-3 text-sm cursor-pointer rounded-lg px-2 py-1.5 hover:bg-white border border-transparent hover:border-slate-200"
                                  >
                                    <input
                                      type="checkbox"
                                      className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
                                      checked={checked}
                                      onChange={(e) => {
                                        const next = new Set(proyForm.consultores_empleado_ids);
                                        if (e.target.checked) next.add(id);
                                        else next.delete(id);
                                        setProyForm((f) => ({
                                          ...f,
                                          consultores_empleado_ids: normalizaIdsConsultores([...next])
                                        }));
                                      }}
                                    />
                                    <span>
                                      <span className="font-medium text-slate-800">{c.nombre_completo}</span>
                                      <span className="text-slate-400 text-xs block">({c.email})</span>
                                    </span>
                                  </label>
                                );
                              })
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-2">
                            Solo aparecen personas dadas de alta como consultores en Administración de usuarios. Al
                            editar un proyecto también ves quien ya estaba asignado aunque se le haya quitado el catálogo.
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            Quienes no estén en el portal pueden mencionarse en «Detalles o comentarios».
                          </p>
                        </>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Horas asignadas *</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm tabular-nums"
                        value={proyForm.horas_asignadas}
                        onChange={(e) => setProyForm((f) => ({ ...f, horas_asignadas: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Estado *</label>
                      <select
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        value={proyForm.estado}
                        onChange={(e) => setProyForm((f) => ({ ...f, estado: e.target.value }))}
                      >
                        {EST_PROY.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Encargado del proyecto *{' '}
                        <span className="text-slate-400 font-normal">(avisos por correo)</span>
                      </label>
                      <select
                        required
                        className="w-full max-w-xl rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        value={proyForm.encargado_empleado_id}
                        onChange={(e) =>
                          setProyForm((f) => ({ ...f, encargado_empleado_id: e.target.value }))
                        }
                      >
                        <option value="">Seleccionar persona…</option>
                        {empleadosEncargado.map((em) => (
                          <option key={em.id} value={em.id}>
                            {[em.apellidos, em.nombres].filter(Boolean).join(', ') || em.email} ({em.email})
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-500 mt-1">
                        Esta persona recibirá un correo cuando alguien{' '}
                        <strong>cree o edite</strong> un registro de horas (actividades) de este proyecto. Puede ser un
                        gerente u otro usuario activo del portal con correo, no tiene que estar en la lista de
                        consultores.
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Detalles o comentarios</label>
                    <textarea
                      rows={3}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      value={proyForm.detalles}
                      onChange={(e) => setProyForm((f) => ({ ...f, detalles: e.target.value }))}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" className="rounded-xl bg-indigo-600 text-white text-sm font-medium px-6 py-2 hover:bg-indigo-700">
                      {proyectoEditId ? 'Actualizar proyecto' : 'Guardar proyecto'}
                    </button>
                    {proyectoEditId && (
                      <button type="button" onClick={resetProyForm} className="rounded-xl border border-slate-200 px-6 py-2 text-sm font-medium">
                        Cancelar edición
                      </button>
                    )}
                  </div>
                </form>
              <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-x-auto">
                <div className="px-6 pt-6 pb-4">
                  <h3 className="text-sm font-semibold text-slate-800 mb-3">Listado de proyectos</h3>
                  <div className="flex flex-col sm:flex-row gap-2 max-w-2xl">
                    <select
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm sm:w-44 shrink-0"
                      value={campoBusquedaProyectos}
                      onChange={(e) => setCampoBusquedaProyectos(e.target.value)}
                      aria-label="Campo de búsqueda de proyectos"
                    >
                      {BUSQUEDA_PROYECTO_CAMPOS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <div className="relative flex-1">
                      <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="search"
                        placeholder={placeholderBusqueda(
                          campoBusquedaProyectos,
                          BUSQUEDA_PROYECTO_CAMPOS,
                          'Buscar en todos los campos…'
                        )}
                        aria-label="Buscar proyectos"
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                        value={busquedaProyectos}
                        onChange={(e) => setBusquedaProyectos(e.target.value)}
                      />
                    </div>
                  </div>
                  {busquedaProyectos.trim() ? (
                    <p className="mt-2 text-xs text-slate-500">
                      {proyectosFiltrados.length} de {proyectos.length} proyecto(s)
                    </p>
                  ) : null}
                </div>
                {proyectos.length === 0 ? (
                  <p className="px-6 pb-6 text-sm text-slate-500">Sin proyectos cargados.</p>
                ) : proyectosFiltrados.length === 0 ? (
                  <p className="px-6 pb-6 text-sm text-slate-500">Ningún proyecto coincide con la búsqueda.</p>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-4 py-2 text-left">Empresa</th>
                        <th className="px-4 py-2 text-left">Proyecto</th>
                        <th className="px-4 py-2 text-left whitespace-nowrap">Inicio–Fin</th>
                        <th className="px-4 py-2 text-left">Encargado</th>
                        <th className="px-4 py-2 text-left">Consultores</th>
                        <th className="px-4 py-2 text-right">Hrs.</th>
                        <th className="px-4 py-2 text-left">Estado</th>
                        <th className="px-4 py-2 text-left whitespace-nowrap min-w-[8.5rem]">Acción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {proyectosFiltrados.map((p) => (
                        <tr key={p.id}>
                          <td className="px-4 py-2">{p.empresa}</td>
                          <td className="px-4 py-2 font-medium">{p.proyecto}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-xs">
                            {formatoFechaDMY(p.fecha_inicio)} – {formatoFechaDMY(p.fecha_fin)}
                          </td>
                          <td className="px-4 py-2 text-xs max-w-[9rem]" title={p.encargado_email || ''}>
                            {p.encargado_nombre || '—'}
                          </td>
                          <td className="px-4 py-2 text-xs max-w-xs">{p.consultores_nombres || '—'}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{Number(p.horas_asignadas).toFixed(2)}</td>
                          <td className="px-4 py-2">{labelEstProy(p.estado)}</td>
                          <td className="px-4 py-2">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                              <button
                                type="button"
                                className="text-indigo-600 text-xs font-medium hover:underline"
                                onClick={() => abrirEditProyecto(p)}
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                className="text-rose-600 text-xs font-medium hover:underline"
                                onClick={() => eliminarProyectoFila(p)}
                              >
                                Eliminar
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {tab === 'actividades' && (
            <>
              <form onSubmit={submitActividad} className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6 mb-8 space-y-4">
                <h2 className="text-lg font-semibold text-slate-800">
                  {actividadEditId ? `Editar actividad #${actividadEditId}` : 'Registro de horas'}
                </h2>
                {actividadEditMeta?.estado_aprobacion === 'rechazada' && (
                  <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
                    <strong>Rechazada.</strong>{' '}
                    {actividadEditMeta.comentario_aprobacion
                      ? actividadEditMeta.comentario_aprobacion
                      : 'Corrija y guarde para reenviar a aprobación.'}
                  </div>
                )}
                {actividadEditMeta?.estado_aprobacion === 'pendiente' && (
                  <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
                    Pendiente de aprobación por el responsable indicado en «Requerido por».
                  </div>
                )}
                <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2 text-sm">
                  <span className="text-slate-500">Consultor (usted): </span>
                  <strong className="text-slate-800">{nombreUsuario || usuario?.email}</strong>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Proyecto *</label>
                    <select
                      required
                      className="w-full max-w-xl rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      value={actForm.proyecto_id}
                      onChange={(e) => {
                        const nuevoProyectoId = e.target.value;
                        /* Si admin estaba con un consultor seleccionado, validamos
                         * que ese consultor también pertenezca al nuevo proyecto.
                         * Si no pertenece, lo limpiamos. */
                        setActForm((f) => {
                          let nuevoConsultor = f.consultor_asignado_id;
                          if (nuevoConsultor) {
                            const proy = proyectosParaActividad.find(
                              (p) => Number(p.id) === Number(nuevoProyectoId)
                            );
                            const ids = proy?.consultores_empleado_ids || [];
                            if (!ids.map(Number).includes(Number(nuevoConsultor))) {
                              nuevoConsultor = '';
                            }
                          }
                          return {
                            ...f,
                            proyecto_id: nuevoProyectoId,
                            consultor_asignado_id: nuevoConsultor
                          };
                        });
                      }}
                    >
                      <option value="">
                        {puedeProy
                          ? 'Seleccione un proyecto (admin: todos los proyectos)'
                          : 'Solo aparecen proyectos donde está asignado'}
                      </option>
                      {proyectosParaActividad.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.empresa} — {p.proyecto}
                        </option>
                      ))}
                    </select>
                  </div>

                  {puedeProy && (
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Consultor asignado (admin)
                      </label>
                      <select
                        className="w-full max-w-xl rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        value={actForm.consultor_asignado_id}
                        onChange={(e) =>
                          setActForm((f) => ({ ...f, consultor_asignado_id: e.target.value }))
                        }
                        disabled={!actForm.proyecto_id}
                      >
                        <option value="">
                          {actForm.proyecto_id
                            ? `Usted (${nombreUsuario || 'admin'}) por defecto`
                            : 'Seleccione primero un proyecto'}
                        </option>
                        {consultoresDelProyectoActivo.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.nombre_completo}
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] text-slate-400 mt-1">
                        Si dejas vacío, la actividad queda registrada a tu nombre. Solo aparecen los
                        consultores asignados al proyecto.
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Requerido por *</label>
                    <select
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      value={actForm.requerido_por}
                      onChange={(e) => {
                        const v = e.target.value;
                        setActForm((f) => ({
                          ...f,
                          requerido_por: v,
                          requerido_por_otros: v === 'otros' ? f.requerido_por_otros : ''
                        }));
                      }}
                    >
                      {requeridoPorSelectOpts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {actForm.requerido_por === 'otros' && (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Nombre (Otros) *
                        </label>
                        <input
                          type="text"
                          maxLength={280}
                          placeholder="Escriba quién requirió la actividad"
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                          value={actForm.requerido_por_otros}
                          onChange={(e) =>
                            setActForm((f) => ({ ...f, requerido_por_otros: e.target.value }))
                          }
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Prioridad *</label>
                    <select
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      value={actForm.prioridad}
                      onChange={(e) => setActForm((f) => ({ ...f, prioridad: e.target.value }))}
                    >
                      {PRIOR.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Fecha y hora inicio *</label>
                    <input
                      type="datetime-local"
                      step={60}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      value={actForm.fecha_hora_inicio}
                      onChange={(e) => setActForm((f) => ({ ...f, fecha_hora_inicio: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Fecha y hora fin *</label>
                    <input
                      type="datetime-local"
                      step={60}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      value={actForm.fecha_hora_fin}
                      onChange={(e) => setActForm((f) => ({ ...f, fecha_hora_fin: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between gap-4 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700">
                          ¿Calcular las horas automáticamente?
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {actForm.horas_manual
                            ? 'Manual: vas a ingresar las horas trabajadas a mano.'
                            : 'Automático: se calculan a partir de la fecha/hora de inicio y fin.'}
                        </div>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!actForm.horas_manual}
                        onClick={() =>
                          setActForm((f) => ({
                            ...f,
                            horas_manual: !f.horas_manual,
                            /* Al activar manual, precargamos con el valor calculado
                             * para que el usuario lo pueda ajustar en vez de empezar
                             * desde cero. Al volver a automático limpiamos. */
                            horas_trabajadas_manual: !f.horas_manual
                              ? f.horas_trabajadas_manual ||
                                (horasPreviewAct != null ? String(horasPreviewAct) : '')
                              : ''
                          }))
                        }
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-200 ${
                          !actForm.horas_manual ? 'bg-indigo-600' : 'bg-slate-300'
                        }`}
                        title={
                          actForm.horas_manual
                            ? 'Activá para volver a cálculo automático'
                            : 'Desactivá para ingresar las horas manualmente'
                        }
                      >
                        <span
                          className={`inline-block h-5 w-5 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
                            !actForm.horas_manual ? 'translate-x-[22px]' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      {actForm.horas_manual
                        ? 'Horas trabajadas (manual) *'
                        : 'Horas trabajadas (calculadas)'}
                    </label>
                    {actForm.horas_manual ? (
                      <input
                        type="number"
                        min="0"
                        step="0.25"
                        inputMode="decimal"
                        placeholder="Ej: 2.5"
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm tabular-nums focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                        value={actForm.horas_trabajadas_manual}
                        onChange={(e) =>
                          setActForm((f) => ({ ...f, horas_trabajadas_manual: e.target.value }))
                        }
                      />
                    ) : (
                      <input
                        readOnly
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-slate-50 tabular-nums"
                        value={horasPreviewAct != null ? String(horasPreviewAct) : '—'}
                      />
                    )}
                    {actForm.horas_manual && horasPreviewAct != null && (
                      <p className="text-[11px] text-slate-400 mt-1">
                        Cálculo automático sugerido: {horasPreviewAct} h.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Estado *</label>
                    <select
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      value={actForm.estado}
                      onChange={(e) => setActForm((f) => ({ ...f, estado: e.target.value }))}
                    >
                      {EST_ACT.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {esAdmin() && (
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Situación de pago (solo administración)
                      </label>
                      <select
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        value={actForm.situacion_pago}
                        onChange={(e) => setActForm((f) => ({ ...f, situacion_pago: e.target.value }))}
                      >
                        {SIT_PAGO.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Descripción de actividad *</label>
                    <textarea
                      required
                      rows={3}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      value={actForm.descripcion_actividad}
                      onChange={(e) => setActForm((f) => ({ ...f, descripcion_actividad: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Comentarios</label>
                    <textarea
                      rows={2}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      value={actForm.comentarios}
                      onChange={(e) => setActForm((f) => ({ ...f, comentarios: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="submit" className="rounded-xl bg-indigo-600 text-white text-sm font-medium px-6 py-2 hover:bg-indigo-700">
                    {actividadEditId ? 'Actualizar actividad' : 'Registrar actividad'}
                  </button>
                  {actividadEditId && (
                    <button
                      type="button"
                      onClick={() => {
                        setActividadEditId(null);
                        setActForm(actividadVacia());
                        setActividadEditMeta(null);
                      }}
                      className="rounded-xl border border-slate-200 px-6 py-2 text-sm font-medium"
                    >
                      Nueva actividad
                    </button>
                  )}
                </div>
              </form>

              <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden">
                <div className="flex flex-col gap-3 px-6 py-4 border-b border-slate-100">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-slate-800">
                      {esAdmin() ? 'Actividades (todas)' : 'Mis actividades'}
                    </h3>
                    <select
                      className="rounded-xl border border-slate-200 px-3 py-2 text-xs"
                      value={filtroProyectoAct}
                      onChange={(e) => setFiltroProyectoAct(e.target.value)}
                    >
                      <option value="">{puedeProy ? 'Todos los proyectos' : 'Todos mis proyectos'}</option>
                      {proyectosParaActividad.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.proyecto}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 max-w-2xl">
                    <select
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm sm:w-44 shrink-0"
                      value={campoBusquedaActividades}
                      onChange={(e) => setCampoBusquedaActividades(e.target.value)}
                      aria-label="Campo de búsqueda de actividades"
                    >
                      {BUSQUEDA_ACTIVIDAD_CAMPOS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <div className="relative flex-1">
                      <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="search"
                        placeholder={placeholderBusqueda(
                          campoBusquedaActividades,
                          BUSQUEDA_ACTIVIDAD_CAMPOS,
                          'Buscar en todos los campos…'
                        )}
                        aria-label="Buscar actividades"
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                        value={busquedaActividades}
                        onChange={(e) => setBusquedaActividades(e.target.value)}
                      />
                    </div>
                  </div>
                  {busquedaActividades.trim() ? (
                    <p className="text-xs text-slate-500">
                      {actividadesFiltradas.length} de {actividades.length} actividad(es)
                    </p>
                  ) : null}
                </div>
                {actividades.length === 0 ? (
                  <p className="p-6 text-sm text-slate-500">No hay registros.</p>
                ) : actividadesFiltradas.length === 0 ? (
                  <p className="p-6 text-sm text-slate-500">Ninguna actividad coincide con la búsqueda.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-4 py-2 text-left whitespace-nowrap">ID</th>
                          <th className="px-4 py-2 text-left">Proyecto</th>
                          <th className="px-4 py-2 text-left">Req. por</th>
                          <th className="px-4 py-2 text-left whitespace-nowrap">Inicio–Fin</th>
                          <th className="px-4 py-2 text-right">Hrs</th>
                          <th className="px-4 py-2 text-left">Estado</th>
                          <th className="px-4 py-2 text-left">Aprobación</th>
                          {esAdmin() && <th className="px-4 py-2 text-left">Pago</th>}
                          <th className="px-4 py-2 text-left">Acción</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {actividadesFiltradas.map((a) => (
                          <tr key={a.id}>
                            <td className="px-4 py-2 text-xs font-mono text-slate-500 whitespace-nowrap">#{a.id}</td>
                            <td className="px-4 py-2 max-w-[200px]" title={`${a.empresa_nombre} · ${a.proyecto_nombre}`}>
                              <div className="font-medium truncate">{a.proyecto_nombre}</div>
                              <div className="text-xs text-slate-500 truncate">{a.empresa_nombre}</div>
                            </td>
                            <td className="px-4 py-2 text-xs">{labelReqActividad(a)}</td>
                            <td className="px-4 py-2 text-xs whitespace-nowrap">
                              {formatoDatetimeBolsaHoras(a.fecha_hora_inicio)}
                              <br />
                              {formatoDatetimeBolsaHoras(a.fecha_hora_fin)}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">{Number(a.horas_trabajadas).toFixed(2)}</td>
                            <td className="px-4 py-2">{EST_ACT.find((x) => x.value === a.estado)?.label}</td>
                            <td className="px-4 py-2">{badgeAprobacion(a.estado_aprobacion)}</td>
                            {esAdmin() && (
                              <td className="px-4 py-2">{SIT_PAGO.find((x) => x.value === a.situacion_pago)?.label}</td>
                            )}
                            <td className="px-4 py-2 whitespace-nowrap space-x-2">
                              <button
                                type="button"
                                className="text-indigo-600 text-xs font-medium hover:underline"
                                onClick={() => abrirEditActividad(a)}
                              >
                                Editar
                              </button>
                              {puedeEliminarActividad(a) && (
                                <button
                                  type="button"
                                  className="text-red-600 text-xs font-medium hover:underline"
                                  onClick={() => eliminarActividadFila(a)}
                                >
                                  Eliminar
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {tab === 'aprobaciones' && (
            <>
              <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden mb-8">
                <div className="px-6 py-4 border-b border-slate-100">
                  <h2 className="text-lg font-semibold text-slate-800">Pendientes de aprobación</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Actividades de locadores que requieren tu visto bueno. También puedes usar los enlaces del correo.
                  </p>
                </div>
                {pendientesAprobacion.length === 0 ? (
                  <p className="p-6 text-sm text-slate-500">No hay pendientes.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-amber-50 text-slate-600">
                        <tr>
                          <th className="px-4 py-2 text-left">ID</th>
                          <th className="px-4 py-2 text-left">Consultor</th>
                          <th className="px-4 py-2 text-left">Proyecto</th>
                          <th className="px-4 py-2 text-left">Descripción</th>
                          <th className="px-4 py-2 text-right">Hrs</th>
                          <th className="px-4 py-2 text-left">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {pendientesAprobacion.map((a) => (
                          <tr key={a.id}>
                            <td className="px-4 py-2 font-mono text-xs">#{a.id}</td>
                            <td className="px-4 py-2">{a.consultor_nombre}</td>
                            <td className="px-4 py-2">
                              <div className="font-medium">{a.proyecto_nombre}</div>
                              <div className="text-xs text-slate-500">{a.empresa_nombre}</div>
                            </td>
                            <td className="px-4 py-2 max-w-[240px] truncate" title={a.descripcion_actividad}>
                              {a.descripcion_actividad}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">{Number(a.horas_trabajadas).toFixed(2)}</td>
                            <td className="px-4 py-2 whitespace-nowrap space-x-2">
                              <button
                                type="button"
                                disabled={procesandoAprobacion}
                                onClick={() => aprobarPendiente(a.id)}
                                className="text-emerald-700 text-xs font-semibold hover:underline disabled:opacity-50"
                              >
                                Aprobar
                              </button>
                              <button
                                type="button"
                                disabled={procesandoAprobacion}
                                onClick={() => {
                                  setRechazoId(a.id);
                                  setRechazoComentario('');
                                }}
                                className="text-red-600 text-xs font-semibold hover:underline disabled:opacity-50"
                              >
                                Rechazar
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {rechazoId != null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                  <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-slate-800">Rechazar actividad #{rechazoId}</h3>
                    <label className="block text-xs font-medium text-slate-600">
                      Comentario para el locador (opcional)
                      <textarea
                        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm min-h-[80px]"
                        value={rechazoComentario}
                        onChange={(e) => setRechazoComentario(e.target.value)}
                        maxLength={500}
                      />
                    </label>
                    <div className="flex gap-2 justify-end">
                      <button
                        type="button"
                        className="rounded-xl border border-slate-200 px-4 py-2 text-sm"
                        onClick={() => {
                          setRechazoId(null);
                          setRechazoComentario('');
                        }}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        disabled={procesandoAprobacion}
                        onClick={() => confirmarRechazo()}
                        className="rounded-xl bg-red-600 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
                      >
                        Confirmar rechazo
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ControlProyectos;
